import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import styles from './index.module.scss';
import { CustomCarousel, SvgIcon } from '@ui-elements';
import {
  desktopMin,
  mobileMax,
  toCamelCase,
  toCssColor,
  toValidUri,
} from '~/common/utils';
import { Loading, Button, IconEnum } from '~/common/components/ui-elements';
import { CallToAction } from '~/common/models';
import stylesHeroCard from '../hero-card/index.module.scss';
import cn from 'classnames';

type VariantAccessoryPacks = {
  header: string;
  description: string;
  fontColor: string;
  disclaimer: string;
  mainImage: string;
  cta: CallToAction[];
  pimIdentifier: string;
};

export const VariantAccessoryPacks = (props: VariantAccessoryPacks) => {
  const [dataList, setDataList] = useState<any[]>([]);
  const [translationWidth, setTranslationWidth] = useState(510);
  const [isLoading, setIsLoading] = useState(true);
  const containerRef = useRef<HTMLDivElement | null>(null);

  const breakpoints = {
    desktop: {
      breakpoint: { max: 4000, min: desktopMin },
      items: 1,
    },
    mobile: {
      breakpoint: { max: mobileMax, min: 0 },
      items: 1,
    },
  };

  const formatAccessoryName = (name: string) => {
    return name
      .replace(/[^a-zA-Z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .replace(/^-|-$/g, '')
      .toLowerCase();
  };

  useLayoutEffect(() => {
    if (containerRef.current !== null) {
      if (containerRef.current?.clientWidth < 361) {
        setTranslationWidth(266);
      } else if (containerRef.current?.clientWidth < 376) {
        setTranslationWidth(280);
      } else if (containerRef.current?.clientWidth < 413) {
        setTranslationWidth(296);
      } else if (containerRef.current?.clientWidth < 745) {
        setTranslationWidth(276);
      } else if (containerRef.current?.clientWidth < 821) {
        setTranslationWidth(290);
      } else if (containerRef.current?.clientWidth < 1025) {
        setTranslationWidth(400);
      }
    }
  }, []);

  useEffect(() => {
    fetch(`/api/accessories/accessory-packs/variant/${props.pimIdentifier}`)
      .then((res) => res.json())
      .then((res) => {
        setDataList(res);
        setIsLoading(false);
      });
  }, []);

  const renderTiles = () => {
    return dataList.map((item, index) => {
      return (
        <div
          className={styles.SliderContent}
          key={index}
          style={{
            ...(dataList?.length > 1 ? {} : { border: 'unset' }),
            ...(dataList?.length > 1 && !item.longDescription
              ? {}
              : { width: 'fit-content' }),
          }}
        >
          <h3>{item.description}</h3>
          <p>{item.longDescription || ''}</p>
          <ul>
            {!!item.accessories &&
              item.accessories.map((bullet, bulletIndex) => {
                const pathnames = window.location.pathname.split('/');
                const formattedAccCategory = formatAccessoryName(
                  bullet.accessoryCategoryName
                );
                const formattedAccName = formatAccessoryName(
                  bullet.description
                );

                const accessoryUrl = pathnames.includes('accessories')
                  ? `/${pathnames[1]}/${pathnames[2]}/${formattedAccCategory}/${formattedAccName}`
                  : `/${pathnames[1]}/accessories/${formattedAccCategory}/${formattedAccName}`;

                return <li key={bulletIndex}>{bullet.description}</li>;
              })}
          </ul>
          {props.cta?.map((cta, idx) => {
            const pathParts = window.location.pathname
              .split('/')
              .filter(Boolean);
            const model = pathParts[0] ?? '';
            const year = /^\d{4}$/.test(pathParts[1] ?? '')
              ? pathParts[1]
              : null;
            const moreInfoUrl = year
              ? `/${model}/${year}/accessory-packs/${item.itemCode.toLowerCase()}/${toValidUri(
                  item.description
                )
                  .replace(/\s+/g, '-')
                  .toLowerCase()}`
              : `/${model}/accessory-packs/${item.itemCode.toLowerCase()}/${toValidUri(
                  item.description
                )
                  .replace(/\s+/g, '-')
                  .toLowerCase()}`;

            const buttonProps: any = {
              type: toCamelCase(cta.ctaButtonType),
              href: moreInfoUrl,
              target: cta.ctaUrl?.target ?? '_self',
              onClick: () => {},
            };
            return (
              <Button
                key={idx}
                className={cn(
                  stylesHeroCard.HeroCardCTA,
                  stylesHeroCard[`HeroCardCTA${buttonProps.type}`]
                )}
                {...buttonProps}
              >
                {cta.ctaIcon && cta.ctaIcon !== 'none' && (
                  <SvgIcon
                    className={styles.HeroCardCTAIcon}
                    type={cta.ctaIcon as keyof typeof IconEnum}
                    color="#1637a0"
                    size={1.2}
                    strokeWidth={2}
                  />
                )}
                <span
                  style={
                    buttonProps.type === 'text' ? { color: '#1637a0' } : {}
                  }
                >
                  {cta.ctaUrl?.name || ''}
                </span>
              </Button>
            );
          })}
        </div>
      );
    });
  };

  return (
    <div
      id="variant-accessory-packs"
      className={styles.AccessoryPackWrapper}
      ref={containerRef}
    >
      <div className={styles.AccessoryPack}>
        <div className={styles.HeaderContainer}>
          <div dangerouslySetInnerHTML={{ __html: props.header }} />
          <div dangerouslySetInnerHTML={{ __html: props.description }} />
        </div>

        {isLoading && <Loading height="100%" />}
        {!isLoading && (
          <div className={styles.ContentContainer}>
            <div className={styles.ContentSlider}>
              {!!dataList && (
                <CustomCarousel
                  dotClassName={styles.CarouselDots}
                  className={styles.CarouselContainer}
                  itemClass={cn(styles.CarouselItem, {
                    [styles.CarouselItemSingle]: dataList?.length === 1,
                  })}
                  responsive={breakpoints}
                  showDots={dataList?.length > 1}
                  removeArrowOnDeviceType={['mobile']}
                  arrows={dataList?.length > 1}
                >
                  {renderTiles()}
                </CustomCarousel>
              )}
            </div>
            <div className={styles.ContentHero}>
              <div style={{ position: 'relative' }}>
                <img src={props.mainImage} alt="" loading="lazy" />
                <div
                  className={styles.ImageDisclaimer}
                  style={{ color: toCssColor(props.fontColor) }}
                  dangerouslySetInnerHTML={{ __html: props.disclaimer }}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
